import { Type, type Static } from "@sinclair/typebox";

export const StructureWorkforceSchema = Type.Object(
  {
    workerAnnualCost: Type.Number({ minimum: 0, maximum: 10000000 }),
    technicianCount: Type.Integer({ minimum: 0, maximum: 10000 }),
    technicianAnnualCost: Type.Number({ minimum: 0, maximum: 10000000 }),
    technicianDirectPercent: Type.Number({ minimum: 0, maximum: 100 }),
    administrativeCount: Type.Integer({ minimum: 0, maximum: 10000 }),
    administrativeAnnualCost: Type.Number({ minimum: 0, maximum: 10000000 }),
    administrativePartTime: Type.Boolean(),
    administrativePercent: Type.Number({ exclusiveMinimum: 0, maximum: 100 }),
  },
  { additionalProperties: false },
);
const BudgetLinkFields = {
  costCenterId: Type.String({ format: "uuid" }),
  version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
};
const EmployeeBudgetLinkSchema = Type.Union([
  Type.Object(
    {
      ...BudgetLinkFields,
      costPerEmployee: Type.Number({ minimum: 0, maximum: 1000000 }),
    },
    { additionalProperties: false },
  ),
  Type.Null(),
]);
export const StructureEmployeeBudgetsSchema = Type.Object(
  {
    dpi: Type.Optional(EmployeeBudgetLinkSchema),
    training: Type.Optional(EmployeeBudgetLinkSchema),
    medical: Type.Optional(EmployeeBudgetLinkSchema),
    damage: Type.Optional(EmployeeBudgetLinkSchema),
  },
  { additionalProperties: false },
);
export type StructureEmployeeBudgetKind =
  "dpi" | "training" | "medical" | "damage";
export type StructureWorkforceParameters = Static<
  typeof StructureWorkforceSchema
>;

export const StructurePlanSaveSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    mode: Type.Union([Type.Literal("capacity"), Type.Literal("projected")]),
    workforce: Type.Optional(
      Type.Union([StructureWorkforceSchema, Type.Null()]),
    ),
    administrativeBudget: Type.Optional(
      Type.Union([
        Type.Object(BudgetLinkFields, { additionalProperties: false }),
        Type.Null(),
      ]),
    ),
    personnelBudget: Type.Optional(
      Type.Union([
        Type.Object(BudgetLinkFields, { additionalProperties: false }),
        Type.Null(),
      ]),
    ),
    employeeBudgets: Type.Optional(
      Type.Union([StructureEmployeeBudgetsSchema, Type.Null()]),
    ),
    workerCount: Type.Integer({ minimum: 1, maximum: 10000 }),
    productiveHoursPerWorker: Type.Number({ minimum: 0.01, maximum: 8784 }),
  },
  { additionalProperties: false },
);
export type StructurePlanSave = Static<typeof StructurePlanSaveSchema>;
export interface StructureAllocationSummary {
  managementYear: number;
  asOf: string;
  totalCost: string;
  budgetCost: string;
  fallbackCost: string;
  workedHours: string;
  openingBalanceHours: string;
  indirectHours: string;
  indirectLaborCost: string;
  unassignedIndirectHours: string;
  projectedHours: string;
  directHourlyAllocation: string;
  projectedHourlyAllocation: string;
  appliedHourlyAllocation: string;
  allocationMode: "capacity" | "projected";
  plan: {
    version: number;
    mode: "capacity" | "projected";
    workerCount: number;
    productiveHoursPerWorker: number;
    workforce?: StructureWorkforceParameters | null;
    administrativeBudget?: { costCenterId: string } | null;
    personnelBudget?: { costCenterId: string } | null;
    employeeBudgets?: Partial<
      Record<
        StructureEmployeeBudgetKind,
        { costCenterId: string; costPerEmployee: number }
      >
    > | null;
  } | null;
}

export const StructureYearPrepareSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    sourceYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    targetYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
  },
  { additionalProperties: false },
);
export type StructureYearPrepare = Static<typeof StructureYearPrepareSchema>;
export const StructureBudgetVisibilitySchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    included: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type StructureBudgetVisibility = Static<
  typeof StructureBudgetVisibilitySchema
>;

const NullableAmount = Type.Union([Type.String(), Type.Null()]);
export const StructureAnnualForecastSchema = Type.Object(
  {
    method: Type.Literal("previous-year-actual-variation"),
    managementYear: Type.Integer({ minimum: 2027, maximum: 2200 }),
    asOf: Type.String({ format: "date" }),
    baseYear: Type.Integer({ minimum: 2026, maximum: 2199 }),
    previousPeriodThrough: Type.String({ format: "date" }),
    status: Type.Union([
      Type.Literal("available"),
      Type.Literal("base-year-open"),
      Type.Literal("missing-base-year"),
      Type.Literal("costs-unreconciled"),
      Type.Literal("undated-costs"),
    ]),
    baseYearClosed: Type.Boolean(),
    baseCost: NullableAmount,
    currentActualCost: Type.String(),
    currentPeriodCost: NullableAmount,
    previousPeriodCost: NullableAmount,
    currentPeriodCostMin: NullableAmount,
    currentPeriodCostMax: NullableAmount,
    previousPeriodCostMin: NullableAmount,
    previousPeriodCostMax: NullableAmount,
    variation: NullableAmount,
    variationMin: NullableAmount,
    variationMax: NullableAmount,
    annualCost: NullableAmount,
    annualCostMin: NullableAmount,
    annualCostMax: NullableAmount,
  },
  { additionalProperties: false },
);
export type StructureAnnualForecast = Static<
  typeof StructureAnnualForecastSchema
>;
